import { Flex, P, Space } from '@dnb/eufemia';
import type { ApiDto } from '@portals/shared/portal/ApiDto';
import { useMemo } from 'react';

import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ServiceDomainResponse } from '@/openapi/schema';
import ServiceDomainFilter from '@/pages/explorer/filters/ServiceDomainFilter';
import StageFilter from '@/pages/explorer/filters/StageFilter';
import { TagFilter } from '@/pages/explorer/filters/TagFilter';
import TypeFilter from '@/pages/explorer/filters/TypeFilter';
import { useCrossFilterCounts } from '@/pages/explorer/filters/useCrossFilterCounts';

import { API_EXPLORER_TABS } from '../../content';
import { useApiFilters } from './useApiFilters';

export function ApiFilters({
  apis,
  apiType,
  isDnbUser,
  serviceDomains,
}: {
  apis: ApiDto[] | undefined;
  apiType: API_EXPLORER_TABS;
  isDnbUser: boolean;
  serviceDomains: ServiceDomainResponse[] | undefined;
}) {
  const { tagFilter, typeOptions, typeFilter, stageFilter, stageOptions, serviceDomainFilter, serviceDomainOptions } =
    useApiFilters(apis, serviceDomains);

  const filters = {
    typeFilter,
    tagFilter,
    stageFilter,
    serviceDomainFilter,
  };

  const filterConfigs = [
    {
      key: 'typeFilter',
      getValue: (api: ApiDto) => api.classification ?? undefined,
    },
    {
      key: 'tagFilter',
      getValue: (api: ApiDto) => api.tags ?? undefined,
    },
    {
      key: 'stageFilter',
      getValue: (api: ApiDto) => api.stage ?? undefined,
    },
    {
      key: 'serviceDomainFilter',
      getValue: (api: ApiDto) => api.serviceNowServiceDomainId ?? undefined,
    },
  ];

  const counts = useCrossFilterCounts(apis ?? [], filters, filterConfigs);

  const tags = useMemo(() => {
    if (!apis) return [];
    return [...new Set(apis.flatMap((api) => api.tags))];
  }, [apis]);

  return (
    <Flex.Vertical>
      {apiType === API_EXPLORER_TABS.Internal && (
        <TypeFilter
          allApiCount={apis?.length}
          classificationCount={counts['typeFilter']!}
          classifications={typeOptions}
        />
      )}

      <Space top="medium">
        {tags.length === 0 ? <P>No tags found</P> : <TagFilter apis={apis} tagCounts={counts['tagFilter']!} />}
      </Space>

      <StageFilter
        selectedStages={stageFilter}
        stageCounts={counts['stageFilter']!}
        stageOptions={stageOptions}
        updateFilterInURL={useUrlFilters().updateFilterInURL}
      />
      {isDnbUser && apiType === API_EXPLORER_TABS.Internal && (
        <ServiceDomainFilter
          serviceDomainCount={counts['serviceDomainFilter']!}
          serviceDomainOptions={serviceDomainOptions}
        />
      )}
    </Flex.Vertical>
  );
}
